import { toast } from 'sonner';
import { API_BASE_URL } from '../config';
import { getBrowserAuthToken } from '../services/authToken';
import { invalidateBrowserAuthSession } from '../services/authSession';

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const handleResponse = async (res: Response, errorPresentation: 'global' | 'caller' = 'global') => {
  if (!res.ok) {
    let errorMessage = res.statusText;
    let errorData = null;

    try {
      const text = await res.text();
      try {
        const json = JSON.parse(text);
        errorData = json;
        errorMessage = json.message || json.error || text;
      } catch {
        errorMessage = text || res.statusText;
      }
    } catch (e) {
      // Failed to read response body
    }

    // Auto-handle 401 Unauthorized (optional, might conflict with Clerk)
    if (res.status === 401) {
       invalidateBrowserAuthSession();
    }

    // Auto-handle 500 Server Error notification
    if (res.status >= 500 && errorPresentation === 'global') {
       toast.error('Server Error: ' + errorMessage);
    }

    throw new ApiError(res.status, errorMessage, errorData);
  }
  return { data: await res.json() };
};

const saveOffline = async (url: string, method: string, body: any) => {
  void body;
  throw new ApiError(409, 'Offline writes require an online authenticated session', {
    offlineBlocked: true,
    method,
    url,
  });
};

/** Build the full URL for an API endpoint (e.g. "/documents" → "/api/documents"). */
function buildUrl(path: string): string {
  // Ensure the path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

/** Retrieve the active identity adapter's auth token (if a session exists). */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getBrowserAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

interface ApiRequestOptions {
  /** Feature owns the visible error and recovery controls. Typed errors still reject. */
  errorPresentation?: 'global' | 'caller';
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export const api = {
  /**
   * Perform a GET request and return parsed JSON.
   * @param url  Relative API path, e.g. "/documents?limit=50"
   */
  get: async (url: string, options?: ApiRequestOptions) => {
    const callerHeaders = Object.fromEntries(
      Object.entries(options?.headers ?? {}).filter(([name]) => name.toLowerCase() !== 'authorization'),
    );
    const headers: Record<string, string> = {
      ...callerHeaders,
      'Content-Type': 'application/json',
      ...(await getAuthHeaders()),
    };

    options?.signal?.throwIfAborted();
    const res = await fetch(buildUrl(url), {
      method: 'GET',
      headers,
      signal: options?.signal,
    });
    return handleResponse(res, options?.errorPresentation);
  },

  /**
   * Perform a POST request with a JSON body.
   */
  post: async (url: string, body?: unknown, options?: ApiRequestOptions) => {
    if (!navigator.onLine) {
       return saveOffline(url, 'POST', body);
    }

    const headers: Record<string, string> = {
      ...(options?.headers ?? {}),
      'Content-Type': 'application/json',
      ...(await getAuthHeaders()),
    };

    options?.signal?.throwIfAborted();
    const res = await fetch(buildUrl(url), {
      method: 'POST',
      headers,
      signal: options?.signal,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return handleResponse(res, options?.errorPresentation);
  },

  /**
   * Perform a PUT request with a JSON body.
   */
  put: async (url: string, body?: unknown, options?: ApiRequestOptions) => {
    if (!navigator.onLine) {
       return saveOffline(url, 'PUT', body);
    }

    const headers: Record<string, string> = {
      ...(options?.headers ?? {}),
      'Content-Type': 'application/json',
      ...(await getAuthHeaders()),
    };

    const res = await fetch(buildUrl(url), {
      method: 'PUT',
      headers,
      signal: options?.signal,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return handleResponse(res);
  },

  /**
   * Perform a DELETE request.
   */
  delete: async (url: string, options?: ApiRequestOptions) => {
    if (!navigator.onLine) {
       return saveOffline(url, 'DELETE', {});
    }

    const headers: Record<string, string> = {
      ...(options?.headers ?? {}),
      'Content-Type': 'application/json',
      ...(await getAuthHeaders()),
    };

    const res = await fetch(buildUrl(url), {
      method: 'DELETE',
      headers,
      signal: options?.signal,
    });
    return handleResponse(res);
  },

  /**
   * Perform a PATCH request with a JSON body.
   */
  patch: async (url: string, body?: unknown, options?: ApiRequestOptions) => {
    if (!navigator.onLine) {
       return saveOffline(url, 'PATCH', body);
    }

    const headers: Record<string, string> = {
      ...(options?.headers ?? {}),
      'Content-Type': 'application/json',
      ...(await getAuthHeaders()),
    };

    const res = await fetch(buildUrl(url), {
      method: 'PATCH',
      signal: options?.signal,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return handleResponse(res, options?.errorPresentation);
  },

  // ---------------------------------------------------------------------------
  // Low-level helpers for non-JSON use-cases (file uploads, blob downloads, …)
  // ---------------------------------------------------------------------------

  /**
   * Perform a fetch with auth headers but return the raw `Response` object.
   * Use this for binary downloads, streaming, or custom content types.
   *
   * @param url     Relative API path, e.g. "/upload/my-file.pdf"
   * @param init    Standard RequestInit overrides (method, body, headers, …)
   */
  fetchRaw: async (url: string, init?: RequestInit): Promise<Response> => {
    const authHeaders = await getAuthHeaders();

    const res = await fetch(buildUrl(url), {
      ...init,
      headers: {
        ...authHeaders,
        ...(init?.headers as Record<string, string> | undefined),
      },
    });

    if (!res.ok) {
      let errorMessage = res.statusText;
      try {
        const text = await res.clone().text();
        try {
          const json = JSON.parse(text);
          errorMessage = json.message || json.error || text;
        } catch {
          errorMessage = text || res.statusText;
        }
      } catch {
        // ignore
      }
      if (res.status === 401) {
        invalidateBrowserAuthSession();
      }
      throw new ApiError(res.status, errorMessage);
    }

    return res;
  },

  /**
   * Upload a file using a PUT to an already-obtained signed URL.
   * Signed URLs are absolute (e.g. from R2) so they bypass `API_BASE_URL`.
   */
  uploadToSignedUrl: async (signedUrl: string, file: File | Blob): Promise<Response> => {
    const res = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });

    if (!res.ok) {
      throw new ApiError(res.status, `Upload failed: ${res.statusText}`);
    }
    return res;
  },
};
